import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useCurrentInternalUser, isAdmin } from '../lib/currentUser';
import Sidebar from '../components/Sidebar';
import ComplianceBadge from '../components/compliance/ComplianceBadge';
import { OttoLoading } from '../components/otto';
import BackIconButton from '../components/BackIconButton';
import { diffVersions, hasChanges } from '../lib/responseDiff';
import { fmtDate, fmtDateTime } from '../lib/dates';
import { formatGBP as gbp } from '../lib/money';

// Single-response full-page view. Mounts at
// /briefs/:id/responses/:supplierId — deep-linkable, more breathing
// room than the inline modal, and a natural landing target from the
// comparison view's column headers. Shows everything the response
// row carries, organised into Quote / Delivery / Notes / Attachments
// sections.
//
// The existing modal in BriefDetail still works for quick scans
// from the recipients list — this page is the canonical detailed
// view for sharing or focused review.

const formatGBP = (n) => gbp(n, { pence: 'auto', fallback: '£0' });
const formatDate = (iso) => fmtDate(iso);
const formatDateTime = (iso) => fmtDateTime(iso);
const formatFileSize = (bytes) => {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

export default function ResponseDetail() {
  const { id: briefId, supplierId } = useParams();
  const navigate = useNavigate();
  const { user: currentInternalUser } = useCurrentInternalUser();
  const [brief, setBrief] = useState(null);
  const [recipient, setRecipient] = useState(null);
  const [response, setResponse] = useState(null);
  const [versions, setVersions] = useState([]);
  const [selectedVersionNum, setSelectedVersionNum] = useState(null); // null = current
  const [showFull, setShowFull] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!briefId || !supplierId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setSelectedVersionNum(null);
      setShowFull(false);
      const [briefRes, recipientRes, responseRes, versionsRes] = await Promise.all([
        supabase
          .from('briefs')
          .select('id, brief_name, fusion_project_number, fusion_project_name, status, awarded_supplier_id, awarded_amount, required_response_time')
          .eq('id', briefId)
          .single(),
        supabase
          .from('brief_recipients')
          .select('id, status, supplier_id, notified_at, responded_at, suppliers(id, supplier_name, category, main_contact_name, main_contact_email, compliance_status)')
          .eq('brief_id', briefId)
          .eq('supplier_id', supplierId)
          .maybeSingle(),
        // Don't use .maybeSingle() — it ERRORS when more than one row
        // matches, which can happen when a supplier saved a draft and
        // later submitted a fresh response (two rows for the same
        // (brief, supplier) pair). Take the best candidate instead:
        // submitted rows beat drafts, then most-recently-submitted, then
        // most-recently-autosaved as a final tiebreaker (brief_responses
        // has no created_at column).
        supabase
          .from('brief_responses')
          .select('*, line_items:brief_response_line_items(*), attachments:brief_response_attachments(*)')
          .eq('brief_id', briefId)
          .eq('supplier_id', supplierId)
          .order('is_submitted', { ascending: false })
          .order('submitted_at', { ascending: false, nullsFirst: false })
          .order('last_autosaved_at', { ascending: false, nullsFirst: false })
          .limit(1),
        // Append-only edit-trail snapshots, oldest → newest.
        supabase
          .from('brief_response_versions')
          .select('*')
          .eq('brief_id', briefId)
          .eq('supplier_id', supplierId)
          .order('version_number', { ascending: true }),
      ]);
      if (cancelled) return;
      const responseRow = (responseRes.data && responseRes.data[0]) || null;
      setBrief(briefRes.data || null);
      setRecipient(recipientRes.data || null);
      setResponse(responseRow);
      setVersions(versionsRes.data || []);
      setNotFound(!recipientRes.data || !responseRow);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [briefId, supplierId]);

  const supplier = recipient?.suppliers || {};
  const isWinner = brief?.status === 'closed' && brief?.awarded_supplier_id === supplierId;
  const canAward = isAdmin(currentInternalUser) && brief?.status === 'sent';

  // --- Edit trail (append-only version snapshots) ---------------------------
  // v1 = original submission, 2+ = edits. The trail UI (tabs + diff) only
  // appears with ≥2 versions; otherwise the page renders the current response
  // exactly as before.
  const sortedVersions = useMemo(
    () => [...versions].sort((a, b) => a.version_number - b.version_number),
    [versions]
  );
  const hasTrail = sortedVersions.length >= 2;
  const currentVersionNum = sortedVersions.length
    ? sortedVersions[sortedVersions.length - 1].version_number
    : null;
  const minVersionNum = sortedVersions.length ? sortedVersions[0].version_number : null;
  const selectedNum = selectedVersionNum ?? currentVersionNum;
  const selectedSnapshot = sortedVersions.find(v => v.version_number === selectedNum) || null;
  const selectedIdx = sortedVersions.findIndex(v => v.version_number === selectedNum);
  const prevSnapshot = selectedIdx > 0 ? sortedVersions[selectedIdx - 1] : null;
  const versionLabel = (num) =>
    num === currentVersionNum ? 'Current' : num === minVersionNum ? 'Original' : `Version ${num}`;
  const diff = hasTrail ? diffVersions(prevSnapshot, selectedSnapshot) : null;
  const editCount = Math.max(0, sortedVersions.length - 1);
  const editedPhrase = editCount === 1 ? 'edited once' : editCount === 2 ? 'edited twice' : `edited ${editCount} times`;

  // What the full Quote/Delivery/Notes/Attachments sections render. With a
  // trail, "Show full version" feeds them from the selected snapshot;
  // otherwise they show the live current response (unchanged behaviour).
  const showingSnapshot = hasTrail && showFull && !!selectedSnapshot;
  const displaySource = useMemo(() => (
    showingSnapshot
      ? {
          is_itemised: selectedSnapshot.is_itemised,
          total_price: selectedSnapshot.total_price,
          vat_rate: selectedSnapshot.vat_rate,
          deadline_date: selectedSnapshot.deadline_date,
          payment_schedule: selectedSnapshot.payment_schedule,
          salient_points: selectedSnapshot.salient_points,
          comments: selectedSnapshot.comments,
          line_items: selectedSnapshot.line_items || [],
          attachments: (selectedSnapshot.document_refs || []).map(d => ({
            file_url: d.file_url, file_name: d.file_name, file_size_bytes: d.file_size_bytes,
          })),
        }
      : response
  ), [showingSnapshot, selectedSnapshot, response]);

  // Quote totals — total_price is the NET subtotal; gross derived from VAT.
  const totals = useMemo(() => {
    if (!displaySource) return null;
    const net = Number(displaySource.total_price) || 0;
    const rate = Number(displaySource.vat_rate) || 0;
    const vat = net * rate / 100;
    return { net, rate, vat, gross: net + vat };
  }, [displaySource]);

  // Line items sorted by their stored sort_order (defensive: not all
  // pre-Phase-C rows have an order, so 0 acts as a neutral fallback).
  const lineItems = useMemo(() => {
    if (!displaySource?.line_items) return [];
    return [...displaySource.line_items].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  }, [displaySource]);

  // Attachments — prefer multi-file rows, fall back to legacy single
  // supporting_doc_url for pre-Phase-B responses (same shape).
  const attachments = useMemo(() => {
    if (!displaySource) return [];
    const multi = (displaySource.attachments || []).slice().sort((a, b) =>
      new Date(a.uploaded_at || 0) - new Date(b.uploaded_at || 0)
    );
    if (multi.length > 0) return multi;
    if (displaySource.supporting_doc_url) {
      return [{
        file_url: displaySource.supporting_doc_url,
        file_name: displaySource.supporting_doc_name || 'Attached file',
        file_size_bytes: null,
      }];
    }
    return [];
  }, [displaySource]);

  return (
    <div className="h-screen bg-canvas flex overflow-hidden font-['Metropolis']">
      <Sidebar currentInternalUser={currentInternalUser} />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header */}
        <div className="px-8 pt-8 pb-4 flex-shrink-0 border-b border-line">
          <div className="mb-3"><BackIconButton to={`/briefs/${briefId}`} label="Back to brief" /></div>
          <div className="flex items-start justify-between gap-6">
            <div className="min-w-0">
              <p className="text-[10px] font-bold tracking-[2px] uppercase text-fusion-forest mb-1.5">Supplier Response</p>
              <h1 className="text-2xl font-bold text-ink mb-1 truncate">
                {supplier.supplier_name || (loading ? 'Loading…' : 'Supplier')}
              </h1>
              {brief && (
                <p className="text-xs text-[#51534A]">
                  {brief.brief_name}
                  {brief.fusion_project_number && <> · {brief.fusion_project_number}</>}
                  {brief.fusion_project_name && <> · {brief.fusion_project_name}</>}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2.5 flex-shrink-0">
              {isWinner && (
                <span className="inline-flex items-center gap-1.5 bg-[#009159]/15 text-[#009159] border border-[#009159]/40 rounded-xl px-3 py-2 text-[11px] font-bold tracking-[2px] uppercase">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden="true">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Awarded
                </span>
              )}
              {canAward && !isWinner && (
                <button
                  type="button"
                  onClick={() => navigate(`/briefs/${briefId}`, { state: { openCompletePanel: true, awardSupplierId: supplierId } })}
                  className="bg-[#92D400] text-[#131313] text-[13px] font-bold rounded-xl px-4 py-2.5 hover:bg-[#a4e810] transition-colors">
                  Award to this supplier
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-8">
          <div className="max-w-4xl mx-auto">
            {loading ? (
              <div className="py-20"><OttoLoading size="large" message="Loading response" /></div>
            ) : notFound ? (
              <div className="bg-white border border-line rounded-xl px-8 py-12 text-center">
                {recipient?.responded_at ? (
                  <>
                    <p className="text-sm text-[#7A4E10] font-semibold mb-2">Response data missing</p>
                    <p className="text-xs text-[#51534A] max-w-md mx-auto leading-relaxed">
                      The supplier was marked responded on {formatDateTime(recipient.responded_at)}, but the response record can't be found. It may have been withdrawn or removed. Contact the supplier or check the submission log.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-fusion-mid-grey mb-2">No submitted response for this supplier.</p>
                    <p className="text-xs text-[#51534A] max-w-md mx-auto">
                      The supplier may not have responded yet, or the response was withdrawn.
                      Head back to the brief to see the full recipient list.
                    </p>
                  </>
                )}
              </div>
            ) : (
              <div className="space-y-6">

                {/* Supplier card — meta + status pills */}
                <div className="bg-white border border-line rounded-xl p-5 flex items-start justify-between gap-6 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2.5 flex-wrap mb-2">
                      <h2 className="text-lg font-bold text-ink truncate">{supplier.supplier_name}</h2>
                      <ComplianceBadge state={supplier.compliance_status} size="sm" />
                      {hasTrail && (
                        <span className="inline-flex items-center bg-[#92D400]/10 text-fusion-forest border border-[#92D400]/30 rounded-xl px-3 py-1 text-[9px] font-bold tracking-[0.12em] uppercase">
                          Responded · {editedPhrase}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-fusion-mid-grey flex items-center gap-1.5 flex-wrap">
                      {supplier.category && <span>{supplier.category}</span>}
                      {supplier.main_contact_name && (
                        <>
                          <span className="text-[#51534A]">·</span>
                          <span>{supplier.main_contact_name}</span>
                        </>
                      )}
                      {supplier.main_contact_email && (
                        <>
                          <span className="text-[#51534A]">·</span>
                          <a href={`mailto:${supplier.main_contact_email}`} className="hover:text-active-green underline-offset-2 hover:underline transition-colors">
                            {supplier.main_contact_email}
                          </a>
                        </>
                      )}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="fusion-field-label">Submitted</p>
                    <p className="text-sm font-bold text-ink">{formatDateTime(response.submitted_at || response.created_at)}</p>
                  </div>
                </div>

                {/* Edit trail — version tabs + diff. Only with ≥2 versions;
                    Fusion-facing only (suppliers never see version history). */}
                {hasTrail && (
                  <section className="bg-white border border-line rounded-xl p-5">
                    <p className="text-xs text-[#51534A] mb-4">
                      First submitted {formatDateTime(sortedVersions[0].saved_at)} · Last edited {formatDateTime(sortedVersions[sortedVersions.length - 1].saved_at)}
                    </p>

                    <p className="fusion-field-label">Version history</p>
                    <div className="flex gap-2 flex-wrap mb-5">
                      {[...sortedVersions].reverse().map(v => {
                        const isSel = v.version_number === selectedNum;
                        return (
                          <button
                            key={v.version_number}
                            type="button"
                            onClick={() => setSelectedVersionNum(v.version_number)}
                            className={`flex flex-col gap-0.5 min-w-[120px] text-left rounded-xl px-3.5 py-2 border transition-colors ${isSel ? 'border-[#92D400] bg-[#92D400]/[0.06]' : 'border-line bg-white hover:border-line'}`}>
                            <span className={`text-[11px] font-bold ${isSel ? 'text-fusion-forest' : 'text-ink'}`}>{versionLabel(v.version_number)}</span>
                            <span className="text-[9px] tracking-[0.1em] uppercase text-[#51534A]">{formatDateTime(v.saved_at)}</span>
                          </button>
                        );
                      })}
                    </div>

                    {diff && diff.isOriginal ? (
                      <p className="text-xs text-[#51534A] italic">This is the original submission — there's no earlier version to compare against.</p>
                    ) : (
                      <>
                        <p className="fusion-field-label">
                          Changes · {versionLabel(prevSnapshot.version_number)} → {versionLabel(selectedNum)}
                        </p>
                        {hasChanges(diff) ? (
                          <div className="space-y-4">
                            {(diff.lineItems.length > 0 || diff.totals) && (
                              <div className="bg-white border border-line rounded-xl px-4 divide-y divide-line">
                                {diff.lineItems.map((row, i) => (
                                  <div key={i} className="flex items-center gap-3 py-2.5 text-sm">
                                    <span className={`w-4 text-center font-extrabold ${row.tag === 'add' ? 'text-fusion-forest' : row.tag === 'rem' ? 'text-[#E0290A]' : 'text-[#7A4E10]'}`}>
                                      {row.tag === 'add' ? '+' : row.tag === 'rem' ? '−' : '~'}
                                    </span>
                                    <span className={`flex-1 ${row.tag === 'add' ? 'text-fusion-forest' : row.tag === 'rem' ? 'text-[#E0290A] line-through' : 'text-ink'}`}>
                                      {row.name}{row.tag === 'add' ? ' (added)' : row.tag === 'rem' ? ' (removed)' : ''}
                                    </span>
                                    <span className="font-bold whitespace-nowrap">
                                      {row.tag === 'chg' && <span className="text-[#51534A] line-through mr-2">{formatGBP(row.was)}</span>}
                                      {row.tag === 'rem'
                                        ? <span className="text-[#51534A] line-through">{formatGBP(row.was)}</span>
                                        : <span className="text-ink">{formatGBP(row.now)}</span>}
                                    </span>
                                  </div>
                                ))}
                                {diff.totals && (
                                  <div className="flex items-center gap-3 py-2.5 text-sm">
                                    <span className="w-4 text-center font-extrabold text-[#7A4E10]">~</span>
                                    <span className="flex-1 text-ink">Grand Total (inc-VAT)</span>
                                    <span className="font-bold whitespace-nowrap">
                                      <span className="text-[#51534A] line-through mr-2">{formatGBP(diff.totals.was)}</span>
                                      <span className="text-ink">{formatGBP(diff.totals.now)}</span>
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}

                            {diff.fields.map((f, i) => (
                              <div key={i}>
                                <p className="text-xs font-bold text-ink mb-1.5">{f.field}</p>
                                {f.was != null && String(f.was).trim() !== '' && (
                                  <p className="text-xs text-[#51534A] line-through leading-relaxed whitespace-pre-wrap">
                                    {f.type === 'date' ? formatDate(f.was) : f.was}
                                  </p>
                                )}
                                <p className="text-xs text-fusion-forest leading-relaxed whitespace-pre-wrap">
                                  {f.now != null && String(f.now).trim() !== ''
                                    ? (f.type === 'date' ? formatDate(f.now) : f.now)
                                    : <span className="text-[#51534A] italic">— cleared —</span>}
                                </p>
                              </div>
                            ))}

                            {diff.documents.length > 0 && (
                              <div>
                                <p className="text-xs font-bold text-ink mb-1.5">Supporting Documents</p>
                                {diff.documents.map((d, i) => (
                                  <p key={i} className="text-xs leading-relaxed">
                                    <span className={`font-extrabold mr-1.5 ${d.tag === 'add' ? 'text-fusion-forest' : 'text-[#E0290A]'}`}>{d.tag === 'add' ? '+' : '−'}</span>
                                    <span className={d.tag === 'add' ? 'text-fusion-forest' : 'text-[#E0290A] line-through'}>
                                      {d.tag === 'add' ? 'Added' : 'Removed'}: {d.name}
                                    </span>
                                  </p>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          // "No FIELD changes" understated what was compared —
                          // the diff also covers line items, totals and
                          // supporting documents, so this reads as "we only
                          // looked at fields" when something else changed.
                          <p className="text-xs text-[#51534A] italic">No changes between these versions — same quote, line items and documents.</p>
                        )}
                      </>
                    )}

                    <button
                      type="button"
                      onClick={() => setShowFull(s => !s)}
                      className="mt-5 text-xs font-semibold text-[#2E8FA1] hover:text-[#3aa9bd] transition-colors">
                      {showFull ? `Hide full version ▴` : `Show ${versionLabel(selectedNum)} in full ▾`}
                    </button>
                  </section>
                )}

                {(!hasTrail || showFull) && (
                <>
                {/* Quote section */}
                <section>
                  <p className="text-[11px] font-bold tracking-[2px] uppercase text-fusion-forest mb-3 pb-2 border-b border-line">Quote</p>
                  <div className="bg-white border border-line rounded-xl p-5">
                    {displaySource.is_itemised && lineItems.length > 0 && (
                      <div className="mb-5">
                        <p className="fusion-field-label">Line Items</p>
                        <div className="space-y-2">
                          {lineItems.map(li => (
                            <div key={li.id || `${li.description}-${li.sort_order}`} className="grid grid-cols-12 gap-2 items-center text-sm">
                              <div className="col-span-7 text-fusion-mid-grey truncate">{li.description || <span className="text-[#51534A] italic">No description</span>}</div>
                              <div className="col-span-2 text-[#51534A] text-right">× {li.qty != null ? li.qty : '0'}</div>
                              <div className="col-span-3 text-ink font-semibold text-right">{formatGBP(li.line_total)}</div>
                            </div>
                          ))}
                        </div>
                        <div className="border-t border-line my-4" />
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-x-8 gap-y-2">
                      <p className="fusion-field-label">Subtotal (ex VAT)</p>
                      <p className="text-sm font-semibold text-fusion-mid-grey text-right">{formatGBP(totals.net)}</p>
                      <p className="fusion-field-label">VAT ({totals.rate}%)</p>
                      <p className="text-sm text-fusion-mid-grey text-right">{formatGBP(totals.vat)}</p>
                      <p className="fusion-field-label mt-1">Grand Total (inc VAT)</p>
                      <p className="text-xl font-extrabold text-fusion-forest text-right">{formatGBP(totals.gross)}</p>
                    </div>
                    <p className="fusion-field-label mt-4 pt-4 border-t border-line">
                      Quote Type · <span className="text-fusion-mid-grey">{displaySource.is_itemised ? 'Itemised' : 'Lump-sum'}</span>
                    </p>
                  </div>
                </section>

                {/* Delivery + Payment */}
                <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white border border-line rounded-xl p-5">
                    <p className="fusion-field-label">Earliest Delivery</p>
                    <p className="text-base font-bold text-ink">{formatDate(displaySource.deadline_date)}</p>
                  </div>
                  <div className="bg-white border border-line rounded-xl p-5">
                    <p className="fusion-field-label">Payment Schedule</p>
                    <p className="text-sm text-ink whitespace-pre-wrap leading-relaxed">
                      {displaySource.payment_schedule || <span className="text-[#51534A] italic">—</span>}
                    </p>
                  </div>
                </section>

                {/* Notes section */}
                <section>
                  <p className="text-[11px] font-bold tracking-[2px] uppercase text-fusion-forest mb-3 pb-2 border-b border-line">Notes</p>
                  <div className="space-y-4">
                    <div className="bg-white border border-line rounded-xl p-5">
                      <p className="fusion-field-label">Salient Points</p>
                      <p className="text-sm text-ink whitespace-pre-wrap leading-relaxed">
                        {displaySource.salient_points || <span className="text-[#51534A] italic">—</span>}
                      </p>
                    </div>
                    <div className="bg-white border border-line rounded-xl p-5">
                      <p className="fusion-field-label">Comments / Caveats</p>
                      <p className="text-sm text-ink italic whitespace-pre-wrap leading-relaxed">
                        {displaySource.comments || <span className="text-[#51534A] not-italic">None</span>}
                      </p>
                    </div>
                  </div>
                </section>

                {/* Attachments */}
                <section>
                  <p className="text-[11px] font-bold tracking-[2px] uppercase text-fusion-forest mb-3 pb-2 border-b border-line">
                    Attachments {attachments.length > 0 && <span className="text-[#51534A] font-normal">· {attachments.length}</span>}
                  </p>
                  <div className="bg-white border border-line rounded-xl p-5">
                    {attachments.length === 0 ? (
                      <p className="text-sm text-[#51534A] italic">No attachments uploaded with this response.</p>
                    ) : (
                      <div className="space-y-2">
                        {attachments.map((a, idx) => (
                          <div key={a.id || idx} className="bg-white border border-line rounded-xl px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
                            <div className="min-w-0 flex-1">
                              <p className="text-sm text-ink truncate">{a.file_name || 'Attached file'}</p>
                              {a.file_size_bytes != null && (
                                <p className="text-xs text-[#51534A] mt-0.5">{formatFileSize(a.file_size_bytes)}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <a
                                href={a.file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-white border border-[#2E8FA1] rounded-xl text-[#2E8FA1] text-xs font-semibold px-3 py-1.5 hover:bg-[#2E8FA1]/10 transition-colors">
                                View
                              </a>
                              <button
                                type="button"
                                onClick={() => {
                                  const link = document.createElement('a');
                                  link.href = a.file_url;
                                  link.download = a.file_name || '';
                                  link.rel = 'noopener noreferrer';
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                }}
                                className="bg-white border border-[#2E8FA1] rounded-xl text-[#2E8FA1] text-xs font-semibold px-3 py-1.5 hover:bg-[#2E8FA1]/10 transition-colors">
                                Download
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </section>
                </>
                )}

              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
